import { format } from 'date-fns'
import { t } from 'i18next'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { AiFillEye } from 'react-icons/ai'
import { toast } from 'react-toastify'

import { useGetReturnReceiptQuery, useUploadReturnReceiptMutation } from '../../api/services/filesApi'
import { PDFDocumentIcon } from '../../assets/icons'
import { RETURN_RECEIPT_STATUS, translateReturnReceiptStatus } from '../../utils/returnReceiptStatus'
import { Button } from '../root/Button'
import { ViewModal } from '../modals/modals'

const ReturnReceiptUploadSection = ({ fileId }) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [openView, setOpenView] = useState(false)
  const [fileAddress, setFileAddress] = useState('')

  const { data, isLoading } = useGetReturnReceiptQuery(fileId, { skip: !fileId })
  const [uploadReturnReceipt, { isLoading: isUploading }] = useUploadReturnReceiptMutation()

  const handleView = () => {
    setFileAddress(data?.attachment?.attachmentUrl || '')
    setOpenView(true)
  }

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0]
    if (!file) return

    if (file.type !== 'application/pdf') {
      toast.error(t('choose_pdf'))
      return
    }

    setSelectedFile(file)
    toast.success(t('file_selected'))
  }, [])

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'application/pdf',
    multiple: false,
    onDrop,
  })

  if (isLoading || !data) return null
  if (data.status === RETURN_RECEIPT_STATUS.NOT_NEEDED) return null

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error(t('choose_doc'))
      return
    }

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)

      await uploadReturnReceipt({ fileId, formData }).unwrap()
      setSelectedFile(null)
      toast.success(t('return_receipt_uploaded'))
    } catch (error) {
      toast.error(error?.data?.message || t('err_occ'))
    }
  }

  if (data.status === RETURN_RECEIPT_STATUS.RETURNED) {
    return (
      <div className="mb-6 p-4 border border-neutral rounded-lg bg-neutral/10">
        <h3 className="font-semibold mb-2">{t('return_receipt')}</h3>
        <p className="text-sm mb-1">
          <span className="font-medium">{t('return_receipt_status')}:</span>{' '}
          {translateReturnReceiptStatus(data.status, t)}
        </p>
        <p className="text-sm mb-1">
          <span className="font-medium">{t('attachment_name')}:</span> {data.attachment?.attachmentName}
        </p>
        {data.returnReceiptUploadedAt ? (
          <p className="text-sm mb-3">
            <span className="font-medium">{t('date')}:</span>{' '}
            {format(new Date(data.returnReceiptUploadedAt), 'dd.MM.yyyy HH:mm')}
          </p>
        ) : null}
        <button
          type="button"
          className="flex items-center gap-2 text-primary text-sm"
          onClick={handleView}
        >
          <AiFillEye />
          {t('view')}
        </button>
        <ViewModal
          id={fileId}
          open={openView}
          onClose={() => setOpenView(false)}
          address={fileAddress}
          page="protocol"
        />
      </div>
    )
  }

  return (
    <div className="mb-6 p-4 border border-neutral rounded-lg bg-neutral/10">
      <h3 className="font-semibold mb-2">{t('return_receipt')}</h3>
      <p className="text-sm mb-3">
        <span className="font-medium">{t('return_receipt_status')}:</span>{' '}
        {translateReturnReceiptStatus(data.status, t)}
      </p>
      <div
        {...getRootProps()}
        className="border-2 border-dashed border-neutral rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer mb-3"
      >
        <input {...getInputProps()} />
        {selectedFile ? (
          <p className="text-sm">{selectedFile.name}</p>
        ) : (
          <>
            <PDFDocumentIcon />
            <p className="text-sm mt-2">{t('return_receipt_upload')}</p>
          </>
        )}
      </div>
      <Button onClick={handleUpload} disabled={!selectedFile || isUploading}>
        {t('return_receipt_upload')}
      </Button>
    </div>
  )
}

export default ReturnReceiptUploadSection
